import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Building2, ShieldCheck, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const maskEmail = (email) => {
  const [local, domain] = email.split("@");
  if (!domain) return email;
  return `${local.slice(0, 2)}***@${domain}`;
};

export default function ContractorLogin() {
  const [step, setStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [approvalId, setApprovalId] = useState(null);
  const pollRef = useRef(null);
  const navigate = useNavigate();

  const clearPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  useEffect(() => {
    if (step !== "approval" || !approvalId) return;
    const finalize = async () => {
      try {
        const r = await axios.post(`${API}/contractors/complete-login/${approvalId}`, {}, { withCredentials: true });
        if (r.data?.id) {
          clearPoll();
          toast.success("Connexion réussie");
          navigate("/espace-entrepreneur");
        }
      } catch (e) { /* still pending */ }
    };
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/contractors/login-approval-status/${approvalId}`);
        if (data.status === "approved") {
          finalize();
        } else if (data.status === "denied") {
          clearPoll(); setStep("credentials"); setApprovalId(null);
          toast.error("Connexion refusée depuis l'e-mail.");
        } else if (data.status === "expired") {
          clearPoll(); setStep("credentials"); setApprovalId(null);
          toast.error("La demande a expiré. Merci de vous reconnecter.");
        }
      } catch (e) { /* ignore transient */ }
    }, 3000);
    return clearPoll;
  }, [step, approvalId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const res = await axios.post(`${API}/contractors/login`, { email, password }, { withCredentials: true });
      if (res.data.requires_verification) {
        setStep("otp");
        toast.info("Votre compte n'est pas encore vérifié. Un code vous a été envoyé par email.");
      } else if (res.data.requires_approval) {
        setApprovalId(res.data.approval_id);
        setStep("approval");
        toast.success("Un e-mail de confirmation vous a été envoyé");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Email ou mot de passe incorrect";
      toast.error(typeof message === "string" ? message : "Email ou mot de passe incorrect");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/contractors/verify-email`, { email, code: otpCode }, { withCredentials: true });
      toast.success("Compte vérifié avec succès !");
      navigate("/espace-entrepreneur");
    } catch (error) {
      const message = error.response?.data?.detail || "Code incorrect";
      toast.error(typeof message === "string" ? message : "Code incorrect");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await axios.post(`${API}/contractors/resend-verification`, { email });
      toast.success("Nouveau code envoyé");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de renvoyer le code");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="contractor-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            {step === "credentials" ? <Building2 className="w-8 h-8 text-white" /> : <ShieldCheck className="w-8 h-8 text-white" />}
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Espace Entrepreneur
          </h1>
          <p className="text-slate-600 mt-2">
            {step === "credentials"
              ? "Connectez-vous à votre espace client"
              : step === "approval"
              ? `Confirmez la connexion dans l'e-mail envoyé à ${maskEmail(email)}`
              : `Code envoyé à ${maskEmail(email)}`}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-8 border border-slate-100">
          {step === "credentials" ? (
            <>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contractor-login-form">
                <div>
                  <Label htmlFor="email" className="text-slate-700 mb-2 block">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="contact@entreprise.be"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="contractor-login-email"
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="text-slate-700 mb-2 block">
                    Mot de passe
                  </Label>
                  <PasswordInput
                    id="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="contractor-login-password"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-6"
                  data-testid="contractor-login-button"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Connexion...
                    </>
                  ) : (
                    "Se connecter"
                  )}
                </Button>

                <div className="text-center">
                  <Link
                    to="/espace-entrepreneur/mot-de-passe-oublie"
                    className="text-sm text-orange-500 hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Mot de passe oublié ?
                  </Link>
                </div>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-slate-600">
                  Pas encore inscrit ?{" "}
                  <Link to="/entrepreneur" className="text-orange-500 font-medium hover:underline">
                    Créer un compte
                  </Link>
                </p>
              </div>
            </>
          ) : step === "approval" ? (
            <div className="space-y-6" data-testid="contractor-approval-step">
              <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800 flex items-start gap-2">
                <MailCheck className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Nous venons de vous envoyer un e-mail. Ouvrez-le et cliquez sur <strong>« Oui, c'est moi »</strong> pour terminer la connexion. Cette page se connectera automatiquement.</span>
              </div>
              <div className="flex flex-col items-center py-2">
                <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                <p className="text-sm text-slate-500 mt-3">En attente de votre confirmation…</p>
              </div>
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => { clearPoll(); setStep("credentials"); setApprovalId(null); }}
                  className="text-slate-500 hover:text-slate-700"
                  data-testid="contractor-approval-back-button"
                >
                  ← Retour
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="text-orange-500 hover:underline"
                  data-testid="contractor-approval-resend-button"
                >
                  Renvoyer l'e-mail
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-6" data-testid="contractor-otp-form">
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-600 flex items-start gap-2">
                <MailCheck className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>
                  Vérifiez votre boîte mail et entrez le code à 6 chiffres pour activer votre compte.
                </span>
              </div>

              <div>
                <Label htmlFor="otpCode" className="text-slate-700 mb-2 block">
                  Code de sécurité (6 chiffres)
                </Label>
                <Input
                  id="otpCode"
                  type="text"
                  inputMode="numeric"
                  placeholder="000000"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                  required
                  className="text-center text-2xl tracking-[0.5em]"
                  maxLength={6}
                  data-testid="contractor-otp-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading || otpCode.length !== 6}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-6"
                data-testid="contractor-otp-verify-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Vérification...
                  </>
                ) : (
                  "Valider le code"
                )}
              </Button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => { setStep("credentials"); setOtpCode(""); }}
                  className="text-slate-500 hover:text-slate-700"
                  data-testid="contractor-otp-back-button"
                >
                  ← Retour
                </button>
                <button
                  type="button"
                  onClick={handleResend}
                  className="text-orange-500 hover:underline"
                  data-testid="contractor-otp-resend-button"
                >
                  Renvoyer le code
                </button>
              </div>
            </form>
          )}
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          <Link to="/" className="hover:text-slate-700">
            ← Retour au site
          </Link>
        </p>
      </div>
    </div>
  );
}
